import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function HomeCaseStudy() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          sectionRef.current?.querySelectorAll('.hcs-fade').forEach((el, i) => {
            setTimeout(() => {
              (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
              (el as HTMLElement).classList.remove('opacity-0', 'translate-y-8');
            }, i * 120);
          });
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28 bg-[#F5F8FF]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="hcs-fade opacity-0 translate-y-8 transition-all duration-700 mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-[#1E6FFF] text-xs tracking-[0.3em] mb-4 uppercase font-medium">/ Case Study</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] tracking-tight leading-tight">
              制作・支援実績
            </h2>
          </div>
          <Link
            to="/case-study"
            className="inline-flex items-center gap-2 text-sm text-[#1E6FFF] font-medium hover:gap-4 transition-all whitespace-nowrap group"
          >
            実績一覧を見る
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Case teaser card */}
        <div
          className="hcs-fade opacity-0 translate-y-8 transition-all duration-700 rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #F5F8FF 0%, #EBF2FF 100%)',
            border: '1px solid rgba(30,111,255,0.15)',
          }}
        >
          {/* Website Preview */}
          <div
            className="relative px-6 md:px-8 pt-6 pb-0 overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #020B18 0%, #061428 100%)' }}
          >
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=dark%20navy%20blue%20abstract%20geometric%20texture%20background%2C%20subtle%20glowing%20grid%20lines%2C%20elegant%20deep%20midnight%20blue%20professional%20design%2C%20soft%20light%20beams%2C%20premium%20dark%20backdrop%20for%20business%20presentation%2C%20minimalist%20dark%20blue%20atmosphere%20with%20faint%20bokeh%20lights&width=900&height=600&seq=casestudy-darkbg-01&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-center opacity-30"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,11,24,0.6), rgba(2,11,24,0.2))' }} />
            </div>
            {/* browser chrome */}
            <div className="relative z-10 flex items-center gap-2 mb-3">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57] shrink-0" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E] shrink-0" />
              <span className="w-3 h-3 rounded-full bg-[#28C840] shrink-0" />
              <div className="flex-1 mx-2 bg-white/10 rounded-md px-3 py-1.5 flex items-center gap-2 max-w-xs">
                <i className="ri-lock-line text-white/40 text-xs" />
                <span className="text-white/40 text-xs font-mono truncate">akihiro-kushikatsu.jp</span>
              </div>
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-white/10 text-white/60 border border-white/10 shrink-0">
                <i className="ri-store-2-line" />
                飲食店 / 熊本市
              </span>
            </div>
            {/* iframe */}
            <div className="relative z-10 rounded-t-xl overflow-hidden w-full h-[320px] bg-white">
              <iframe
                src="https://readdy.cc/preview/41d580cf-b466-4484-b234-e6188398962d/7862685/"
                title="串カツ あき×ひろ ウェブサイト"
                className="absolute top-0 left-0"
                style={{
                  width: '167%',
                  height: '167%',
                  transform: 'scale(0.6)',
                  transformOrigin: 'top left',
                  border: 'none',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>

          {/* Details */}
          <div
            className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x"
            style={{ borderColor: 'rgba(30,111,255,0.12)' }}
          >
            {/* Left */}
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs text-[#1E6FFF] font-semibold tracking-widest uppercase">No. 01</span>
                <span className="h-px flex-1" style={{ background: 'rgba(30,111,255,0.2)' }} />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628] leading-snug mb-1">
                串カツ あき×ひろ 様
              </h3>
              <p className="text-[#1E6FFF] text-sm font-medium mb-4">熊本市 ／ 飲食店</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {['WEBサイト制作', 'MEO対策', '月額保守'].map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full font-medium"
                    style={{
                      background: 'rgba(30,111,255,0.08)',
                      color: '#1E6FFF',
                      border: '1px solid rgba(30,111,255,0.2)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-sm text-[#555] leading-relaxed">
                Readdy AIを活用したスピード公開、Googleマップ（MEO）対策による認知拡大、月額保守による安定運用の3つのアプローチで集客を最大化。
              </p>
            </div>

            {/* Right */}
            <div className="p-8 md:p-10 flex flex-col justify-between gap-6">
              <div
                className="flex items-start gap-3 p-5 rounded-2xl text-white"
                style={{ background: 'linear-gradient(135deg, #1E6FFF 0%, #0099FF 100%)' }}
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/20 shrink-0 mt-0.5">
                  <i className="ri-award-line text-base text-white" />
                </div>
                <p className="text-sm font-semibold leading-relaxed">
                  公開後3ヶ月でGoogleマップの露出が大幅増加
                </p>
              </div>
              <Link
                to="/case-study"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap hover:text-white"
                style={{
                  border: '1px solid #1E6FFF',
                  color: '#1E6FFF',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.background = 'linear-gradient(90deg, #1E6FFF, #0099FF)';
                  (e.currentTarget as HTMLAnchorElement).style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                  (e.currentTarget as HTMLAnchorElement).style.color = '#1E6FFF';
                }}
              >
                <i className="ri-eye-line" />
                詳細を見る
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
